import { Form } from 'antd'
import dayjs from 'dayjs'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import { useStocksServiceGetInquiryEndpoint } from 'src/api/queries'
import {
  InventoryDocumentInquiryItemResponse,
  InventoryDocumentState,
  InventoryItemResponse
} from 'src/api/requests'
import { LoadingScreen, PageBreadcrumbs } from 'src/components'
import {
  StockAdminIssuanceApplicationsAcceptedDetails,
  StockAdminIssuanceApplicationsWaiting
} from 'src/features/stock-administration/components'
import { useFileHandler } from 'src/hooks'
import { useLocales } from 'src/locales'

type FormattedInquiryItem = {
  modifiedQuantity: number
  items: InventoryItemResponse[]
} & InventoryDocumentInquiryItemResponse

export const StockAdminIssuanceApplicationsDetails = () => {
  const [form] = Form.useForm()

  const { t, currentLang } = useLocales()

  const { id } = useParams()

  const { setExistingFiles } = useFileHandler({
    form,
    folder: id ? `issuance-${id}` : 'inquiries'
  })

  const { data, isLoading } = useStocksServiceGetInquiryEndpoint(
    {
      id: String(id)
    },
    [String(id)],
    {
      enabled: !!id
    }
  )

  const [isEdit, setIsEdit] = useState(
    data?.state === InventoryDocumentState.ACCEPTED
  )

  const isSubmitted =
    !!data?.relatedDocuments?.length ||
    data?.state === InventoryDocumentState.REJECTED

  useEffect(() => {
    if (data) {
      const existingFileIds = data?.mediaFiles
        ?.map((file) => file?.id)
        .filter(Boolean) as string[]

      setExistingFiles(existingFileIds)

      const isModifiedAndAccepted =
        data.state === InventoryDocumentState.MODIFIED_AND_ACCEPTED

      let inquiryItems: FormattedInquiryItem[] = []

      if (isModifiedAndAccepted) {
        const mergedMap = new Map<string, FormattedInquiryItem>()

        for (const item of data.items || []) {
          const key = item?.internationalName?.id ?? ''

          const existing = mergedMap?.get(key) ?? {
            name: `${item?.internationalName?.name} ${item?.internationalName?.dosage}`,
            internationalNameId: item?.internationalName?.id,
            form: item?.productForm,
            quantity: 0,
            modifiedQuantity: 0,
            items: []
          }

          if (item?.isArchived) {
            existing.quantity = item?.quantity
          } else {
            existing.modifiedQuantity = item?.quantity ?? 0
          }

          existing.items = [
            ...(existing?.items || []),
            ...(item?.beneficiaryStocks || [])
          ]
          mergedMap.set(key, existing)
        }

        inquiryItems = Array.from(mergedMap.values())
      } else {
        inquiryItems = (data.items || []).map((item) => ({
          name: `${item?.internationalName?.name} ${item?.internationalName?.dosage}`,
          internationalNameId: item?.internationalName?.id,
          form: item?.productForm,
          quantity: item?.quantity ?? 0,
          modifiedQuantity: 0,
          items: item.beneficiaryStocks || []
        }))
      }

      const initialFormValues = {
        documentDate: data?.documentDate
          ? dayjs(data?.documentDate)
          : undefined,
        documentNumber: data?.documentNumber,
        supplier: {
          name: data?.supplier?.name,
          id: data?.supplier?.id
        },
        beneficiary: {
          name: data?.beneficiary?.name,
          id: data?.beneficiary?.id
        },
        updateReasonId: data?.updateReason?.translation?.[currentLang.value],
        docs: existingFileIds.map((id) => ({
          uid: id,
          name: `File-${id}`,
          status: 'done'
        })),
        inquiryItems
      }

      form.setFieldsValue(initialFormValues)
    }
  }, [data])

  if (isLoading) {
    return <LoadingScreen />
  }

  return (
    <PageBreadcrumbs
      title={
        isEdit
          ? t('stock_applications.liberation_document')
          : `${t('stock_applications.inquiry_number')}: #${data?.code}`
      }
      items={[
        { title: t('menu.consumable_medicaments') },
        { title: t('stock.applications') },
        {
          title: isEdit
            ? t('stock_applications.liberation_document')
            : `${t('stock_applications.inquiry_number')}: #${data?.code}`
        }
      ]}
    >
      <Form form={form} layout="vertical" disabled={isSubmitted}>
        {isSubmitted ? (
          <StockAdminIssuanceApplicationsAcceptedDetails
            data={data}
            form={form}
          />
        ) : (
          <StockAdminIssuanceApplicationsWaiting
            data={data}
            form={form}
            isEdit={isEdit}
            setIsEdit={setIsEdit}
          />
        )}
      </Form>
    </PageBreadcrumbs>
  )
}
